/** @format */
import {
    JourneyModel,
    ModelPurchaseJourney,
} from '@toucan/common/features/beta-model-insights/model-journey';

import {
    CARD_TAGLINE_LIMIT,
    FAN_MODEL_COUNT,
    TimelineSide,
} from '../config/constants';
import { TimelineSelection, TimelineSelectionShare } from '../config/types';

/**
 * A tagline as a card will show it: cut to CARD_TAGLINE_LIMIT characters with an
 * ellipsis after them, and only then. Anything already inside the limit is left
 * exactly as it came, without so much as a trailing space touched.
 *
 * The cut is by character rather than by word, so it can land mid-word. That is the
 * honest signal that the text was longer than a card can take.
 */
export function truncateTagline(tagline: string): string {
    if (tagline.length <= CARD_TAGLINE_LIMIT) {
        return tagline;
    }

    return `${tagline.slice(0, CARD_TAGLINE_LIMIT).trimEnd()}...`;
}

/**
 * The models one hand of the fan shows. Only the most common models make it
 * on: a hand with more cards than this stops reading as a fan.
 */
export function getFanModels(models: readonly JourneyModel[]): JourneyModel[] {
    return models.slice(0, FAN_MODEL_COUNT);
}

/**
 * The model a model ID refers to, and its share in every hand it appears in.
 *
 * A model in both hands returns both shares, in reading order, so the detail card
 * can say that customers both owned it before this model and bought it after —
 * with the different percentage each of those is.
 *
 * Searches only the models the fan actually renders, so a selection can never
 * point at a model the reader cannot see.
 */
export function findFanSelection(
    journey: ModelPurchaseJourney,
    selectedModelId: number | null
): TimelineSelection | null {
    if (selectedModelId === null) {
        return null;
    }

    const findIn = (models: readonly JourneyModel[]) =>
        getFanModels(models).find((model) => model.id === selectedModelId);

    const precedingModel = findIn(journey.precededBy);
    const followingModel = findIn(journey.followedBy);
    const model = precedingModel ?? followingModel;

    if (!model) {
        return null;
    }

    const shares: TimelineSelectionShare[] = [];

    if (precedingModel) {
        shares.push({ side: TimelineSide.BEFORE, pct: precedingModel.pct });
    }

    if (followingModel) {
        shares.push({ side: TimelineSide.AFTER, pct: followingModel.pct });
    }

    return { model, shares };
}
